import { useCallback, useEffect, useRef } from 'react';
import { subscribe } from 'lib/pusher';
import { useLatest } from 'lib/hooks/use-latest';
import { RELAND_EVENT, type RelandEvent } from './utils';

/** The part of a pusher-js `Channel` the reland watch touches. */
interface RelandChannel {
  bind: (event: string, handler: (evt: RelandEvent) => void) => void;
  unbind: (event: string) => void;
  unsubscribe: () => void;
  /** True once pusher-js has confirmed the subscription. */
  subscribed?: boolean;
}

/** pusher-js's own event once a channel subscription is confirmed. */
const SUBSCRIBED_EVENT = 'pusher:subscription_succeeded';

/**
 * Follows re-landed wells on the workspace channel (Story 1).
 *
 * A permit moved in depth is re-landed on the request, but its landing analysis and any
 * formation change resolve afterwards on the workspace channel. One subscription outlives the
 * batch that opened it, until every landing edit it is waiting on has reported; a later batch
 * joins the same subscription, and so do the edits a page opened on still finds pending (a
 * landing started before a reload). Unmounting drops it.
 *
 *
 * The landing job is already queued when the watch is armed, and a fast failure can report before
 * the subscription is live; Pusher does not replay it. So once the subscription is confirmed the
 * watch calls `onSubscribed`, which re-reads the server state — any report missed before then is
 * already in it (the edit's status is what the chart reads), and every report after it arrives.
 *
 * @param {number} inventoryWorkspaceId - The workspace whose channel the relands report on.
 * @param {Function} onResolved - Called once for each watched landing edit, as it reports.
 * @param {Function} onSubscribed - Called once the subscription is live, to reconcile.
 * @return {Function} Starts watching the given landing edit ids.
 */
export function useRelandWatch(
  inventoryWorkspaceId: number,
  onResolved: (evt: RelandEvent) => void,
  onSubscribed?: () => void
): (editIds: number[]) => void {
  const onResolvedRef = useLatest(onResolved);
  const onSubscribedRef = useLatest(onSubscribed);
  const watchRef = useRef<{
    channel: RelandChannel;
    pending: Set<number>;
  } | null>(null);

  const stopWatching = useCallback(() => {
    watchRef.current?.channel.unbind(SUBSCRIBED_EVENT);
    watchRef.current?.channel.unbind(RELAND_EVENT);
    watchRef.current?.channel.unsubscribe();
    watchRef.current = null;
  }, []);
  useEffect(() => stopWatching, [stopWatching]);

  return useCallback(
    (editIds: number[]) => {
      if (editIds.length === 0) return;
      if (!watchRef.current) {
        const channel = subscribe(
          `inventory_workspace_${inventoryWorkspaceId}`
        ) as RelandChannel;
        const pending = new Set<number>();
        watchRef.current = { channel, pending };
        channel.bind(RELAND_EVENT, (evt) => {
          const id = evt.landingEditId;
          if (id === undefined || !pending.has(id)) return;
          pending.delete(id);
          onResolvedRef.current(evt);
          if (pending.size === 0) stopWatching();
        });
        if (channel.subscribed) onSubscribedRef.current?.();
        else channel.bind(SUBSCRIBED_EVENT, () => onSubscribedRef.current?.());
      }
      editIds.forEach((id) => watchRef.current?.pending.add(id));
    },
    [inventoryWorkspaceId, onResolvedRef, onSubscribedRef, stopWatching]
  );
}
